import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAppointmentSettingsThunk,
  toggleAcceptingAppointmentsThunk,
} from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Input from "../../../../components/common/Input";
import NumberInput from "../../../../components/common/NumberInput";
import Checkbox from "../../../../components/common/Checkbox";
import {
  CogIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
  UserGroupIcon,
  CalendarIcon,
} from "@heroicons/react/outline";

const AppointmentSettings = ({
  settings: initialSettings,
  acceptingAppointments: initialAccepting,
  admin,
}) => {
  const dispatch = useDispatch();
  const { loading, appointmentTypes, specializations } = useSelector(
    (state) => state.admin
  );

  // Local state
  const [acceptingAppointments, setAcceptingAppointments] = useState(
    initialAccepting || false
  );
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [preferences, setPreferences] = useState({
    defaultDuration: 30,
    maxAppointmentsPerDay: 8,
    bufferTimeBetweenAppointments: 10,
    allowWalkIns: true,
    advanceBookingDays: 30,
    minAdvanceBookingHours: 2,
    allowOnlineBooking: true,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  // Appointment type options
  const appointmentTypeOptions = appointmentTypes || [
    {
      value: "PRODUCT_CONSULTATION",
      label: "Product Consultation",
      icon: "💍",
      description: "Help customers choose jewelry pieces",
    },
    {
      value: "CUSTOM_DESIGN",
      label: "Custom Design",
      icon: "✨",
      description: "Design custom jewelry pieces",
    },
    {
      value: "REPAIR_CONSULTATION",
      label: "Repair Consultation",
      icon: "🔧",
      description: "Assess repair needs",
    },
    {
      value: "APPRAISAL",
      label: "Appraisal",
      icon: "💎",
      description: "Value assessment services",
    },
    {
      value: "ENGAGEMENT_RING",
      label: "Engagement Ring",
      icon: "💍",
      description: "Engagement ring selection",
    },
    {
      value: "WEDDING_JEWELRY",
      label: "Wedding Jewelry",
      icon: "💒",
      description: "Wedding jewelry consultation",
    },
    {
      value: "GENERAL_INQUIRY",
      label: "General Inquiry",
      icon: "❓",
      description: "General questions and browsing",
    },
    {
      value: "VIP_CLIENT",
      label: "VIP Client",
      icon: "⭐",
      description: "VIP customer appointments",
    },
    {
      value: "COLLECTION_VIEWING",
      label: "Collection Viewing",
      icon: "👁️",
      description: "View special collections",
    },
  ];

  // Specialization options
  const specializationOptions = specializations || [
    {
      value: "DIAMONDS",
      label: "Diamonds",
      icon: "💎",
      description: "Diamond expertise and selection",
    },
    {
      value: "GOLD_JEWELRY",
      label: "Gold Jewelry",
      icon: "🥇",
      description: "Gold jewelry specialist",
    },
    {
      value: "CUSTOM_DESIGN",
      label: "Custom Design",
      icon: "✨",
      description: "Custom jewelry design",
    },
    {
      value: "ANTIQUE_JEWELRY",
      label: "Antique Jewelry",
      icon: "🏺",
      description: "Antique and vintage pieces",
    },
    {
      value: "GEMSTONES",
      label: "Gemstones",
      icon: "💠",
      description: "Precious gemstone expertise",
    },
    {
      value: "WEDDING_COLLECTIONS",
      label: "Wedding Collections",
      icon: "💒",
      description: "Wedding jewelry specialist",
    },
    {
      value: "REPAIRS",
      label: "Repairs",
      icon: "🔧",
      description: "Jewelry repair services",
    },
    {
      value: "VALUATION",
      label: "Valuation",
      icon: "📊",
      description: "Jewelry appraisal and valuation",
    },
  ];

  // Initialize from props
  useEffect(() => {
    if (initialSettings) {
      setSelectedAppointmentTypes(initialSettings.appointmentTypes || []);
      setSelectedSpecializations(initialSettings.specializations || []);
      setPreferences(
        initialSettings.preferences || {
          defaultDuration: 30,
          maxAppointmentsPerDay: 8,
          bufferTimeBetweenAppointments: 10,
          allowWalkIns: true,
          advanceBookingDays: 30,
          minAdvanceBookingHours: 2,
          allowOnlineBooking: true,
        }
      );
    }
    setAcceptingAppointments(initialAccepting || false);
  }, [initialSettings, initialAccepting]);

  // Track changes
  useEffect(() => {
    if (initialSettings) {
      const settingsChanged =
        JSON.stringify(selectedAppointmentTypes) !==
          JSON.stringify(initialSettings.appointmentTypes || []) ||
        JSON.stringify(selectedSpecializations) !==
          JSON.stringify(initialSettings.specializations || []) ||
        JSON.stringify(preferences) !==
          JSON.stringify(initialSettings.preferences || {});

      const acceptingChanged = acceptingAppointments !== initialAccepting;

      setHasChanges(settingsChanged || acceptingChanged);
    }
  }, [
    selectedAppointmentTypes,
    selectedSpecializations,
    preferences,
    acceptingAppointments,
    initialSettings,
    initialAccepting,
  ]);

  // Toggle appointment type
  const toggleAppointmentType = (type) => {
    setSelectedAppointmentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Toggle specialization
  const toggleSpecialization = (spec) => {
    setSelectedSpecializations((prev) =>
      prev.includes(spec) ? prev.filter((s) => s !== spec) : [...prev, spec]
    );
  };

  // Update preference
  const updatePreference = (key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  // Create a helper function to get active errors (excluding null/undefined)
  const getActiveErrorsCount = (errorsObj) => {
    return Object.values(errorsObj).filter(
      (error) => error !== null && error !== undefined
    ).length;
  };

  // Validate preferences
  const validatePreferences = () => {
    const newErrors = {};

    if (preferences.defaultDuration < 10 || preferences.defaultDuration > 240) {
      newErrors.defaultDuration = "Duration must be between 10 and 240 minutes";
    }

    if (
      preferences.maxAppointmentsPerDay < 1 ||
      preferences.maxAppointmentsPerDay > 20
    ) {
      newErrors.maxAppointmentsPerDay = "Must be between 1 and 20";
    }

    if (
      preferences.bufferTimeBetweenAppointments < 0 ||
      preferences.bufferTimeBetweenAppointments > 60
    ) {
      newErrors.bufferTimeBetweenAppointments =
        "Must be between 0 and 60 minutes";
    }

    if (
      preferences.advanceBookingDays < 1 ||
      preferences.advanceBookingDays > 90
    ) {
      newErrors.advanceBookingDays = "Must be between 1 and 90 days";
    }

    if (
      preferences.minAdvanceBookingHours < 0 ||
      preferences.minAdvanceBookingHours > 72
    ) {
      newErrors.minAdvanceBookingHours = "Must be between 0 and 72 hours";
    }

    setErrors(newErrors);
    return getActiveErrorsCount(newErrors) === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validatePreferences()) return;

    try {
      // Update appointment settings
      await dispatch(
        updateAppointmentSettingsThunk({
          appointmentTypes: selectedAppointmentTypes,
          specializations: selectedSpecializations,
          preferences,
        })
      ).unwrap();

      // Update accepting appointments if changed
      if (acceptingAppointments !== initialAccepting) {
        await dispatch(
          toggleAcceptingAppointmentsThunk(acceptingAppointments)
        ).unwrap();
      }

      setHasChanges(false);
    } catch (error) {
      console.error("Failed to update appointment settings:", error);
    }
  };

  // Handle reset
  const handleReset = () => {
    if (window.confirm("Discard all changes?")) {
      setSelectedAppointmentTypes(initialSettings?.appointmentTypes || []);
      setSelectedSpecializations(initialSettings?.specializations || []);
      setPreferences(initialSettings?.preferences || {});
      setAcceptingAppointments(initialAccepting || false);
      setErrors({});
      setHasChanges(false);
    }
  };

  // Quick preset configurations
  const applyPreset = (presetName) => {
    const presets = {
      beginner: {
        appointmentTypes: ["PRODUCT_CONSULTATION", "GENERAL_INQUIRY"],
        specializations: ["GOLD_JEWELRY"],
        preferences: {
          defaultDuration: 30,
          maxAppointmentsPerDay: 4,
          bufferTimeBetweenAppointments: 15,
          allowWalkIns: false,
          advanceBookingDays: 14,
          minAdvanceBookingHours: 4,
          allowOnlineBooking: true,
        },
      },
      standard: {
        appointmentTypes: [
          "PRODUCT_CONSULTATION",
          "ENGAGEMENT_RING",
          "GENERAL_INQUIRY",
          "COLLECTION_VIEWING",
        ],
        specializations: ["DIAMONDS", "GOLD_JEWELRY"],
        preferences: {
          defaultDuration: 45,
          maxAppointmentsPerDay: 6,
          bufferTimeBetweenAppointments: 10,
          allowWalkIns: true,
          advanceBookingDays: 30,
          minAdvanceBookingHours: 2,
          allowOnlineBooking: true,
        },
      },
      expert: {
        appointmentTypes: [
          "PRODUCT_CONSULTATION",
          "CUSTOM_DESIGN",
          "ENGAGEMENT_RING",
          "WEDDING_JEWELRY",
          "VIP_CLIENT",
          "APPRAISAL",
        ],
        specializations: [
          "DIAMONDS",
          "GOLD_JEWELRY",
          "CUSTOM_DESIGN",
          "GEMSTONES",
        ],
        preferences: {
          defaultDuration: 60,
          maxAppointmentsPerDay: 8,
          bufferTimeBetweenAppointments: 15,
          allowWalkIns: true,
          advanceBookingDays: 60,
          minAdvanceBookingHours: 2,
          allowOnlineBooking: true,
        },
      },
    };

    if (presets[presetName]) {
      setSelectedAppointmentTypes(presets[presetName].appointmentTypes);
      setSelectedSpecializations(presets[presetName].specializations);
      setPreferences(presets[presetName].preferences);
    }
  };

  return (
    <div className="space-y-6">
      {/* Accepting Appointments Toggle */}
      <div
        className={`border-2 rounded-lg p-6 ${
          acceptingAppointments
            ? "border-green-200 bg-green-50"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                acceptingAppointments ? "bg-green-500" : "bg-gray-400"
              }`}
            >
              {acceptingAppointments ? (
                <CheckCircleIcon className="w-6 h-6 text-white" />
              ) : (
                <ExclamationCircleIcon className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {acceptingAppointments
                  ? "Accepting Appointments"
                  : "Not Accepting Appointments"}
              </h3>
              <p className="text-sm text-gray-600">
                {acceptingAppointments
                  ? "Customers can book appointments with you"
                  : "Appointment booking is currently disabled"}
              </p>
            </div>
          </div>
          <Button
            variant={acceptingAppointments ? "danger" : "success"}
            onClick={() => setAcceptingAppointments(!acceptingAppointments)}
          >
            {acceptingAppointments ? "Disable" : "Enable"}
          </Button>
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <SparklesIcon className="w-4 h-4 text-primary" />
          Quick Presets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => applyPreset("beginner")}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">🌱 Beginner</p>
            <p className="text-xs text-gray-600">
              Basic appointments, limited types, 4 per day
            </p>
          </button>
          <button
            onClick={() => applyPreset("standard")}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">⚡ Standard</p>
            <p className="text-xs text-gray-600">
              Popular types, walk-ins allowed, 6 per day
            </p>
          </button>
          <button
            onClick={() => applyPreset("expert")}
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">🏆 Expert</p>
            <p className="text-xs text-gray-600">
              All types, multiple specializations, 8 per day
            </p>
          </button>
        </div>
      </div>

      {/* Appointment Types */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-primary" />
          Appointment Types You Handle
          <Badge variant="info">
            {selectedAppointmentTypes.length} selected
          </Badge>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {appointmentTypeOptions.map((type) => {
            const isSelected = selectedAppointmentTypes.includes(type.value);
            return (
              <button
                key={type.value}
                onClick={() => toggleAppointmentType(type.value)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  isSelected
                    ? "border-primary bg-blue-50 ring-2 ring-primary ring-offset-1"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{type.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {type.label}
                      {isSelected && (
                        <CheckCircleIcon className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {type.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Specializations */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <SparklesIcon className="w-4 h-4 text-primary" />
          Your Specializations
          <Badge variant="success">
            {selectedSpecializations.length} selected
          </Badge>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {specializationOptions.map((spec) => {
            const isSelected = selectedSpecializations.includes(spec.value);
            return (
              <button
                key={spec.value}
                onClick={() => toggleSpecialization(spec.value)}
                className={`p-3 border-2 rounded-lg text-left transition-all ${
                  isSelected
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <span className="text-2xl">{spec.icon}</span>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {spec.label}
                    </p>
                    {isSelected && (
                      <CheckCircleIcon className="w-4 h-4 text-green-600 mx-auto mt-1" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preferences */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CogIcon className="w-4 h-4 text-primary" />
          Appointment Preferences
        </h3>
        <div className="bg-gray-50 rounded-lg p-6 border space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Duration (minutes)
              </label>
              <NumberInput
                value={preferences.defaultDuration}
                onChange={(value) => updatePreference("defaultDuration", value)}
                min={10}
                max={240}
                suffix="min"
              />
              {errors.defaultDuration && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.defaultDuration}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Time allocated per appointment
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Appointments Per Day
              </label>
              <NumberInput
                value={preferences.maxAppointmentsPerDay}
                onChange={(value) =>
                  updatePreference("maxAppointmentsPerDay", value)
                }
                min={1}
                max={20}
              />
              {errors.maxAppointmentsPerDay && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.maxAppointmentsPerDay}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Maximum daily appointment limit
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buffer Time Between (minutes)
              </label>
              <NumberInput
                value={preferences.bufferTimeBetweenAppointments}
                onChange={(value) =>
                  updatePreference("bufferTimeBetweenAppointments", value)
                }
                min={0}
                max={60}
                suffix="min"
              />
              {errors.bufferTimeBetweenAppointments && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.bufferTimeBetweenAppointments}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Break time between appointments
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Advance Booking Days
              </label>
              <NumberInput
                value={preferences.advanceBookingDays}
                onChange={(value) =>
                  updatePreference("advanceBookingDays", value)
                }
                min={1}
                max={90}
                suffix="days"
              />
              {errors.advanceBookingDays && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.advanceBookingDays}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                How far ahead customers can book
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Advance Booking (hours)
              </label>
              <NumberInput
                value={preferences.minAdvanceBookingHours}
                onChange={(value) =>
                  updatePreference("minAdvanceBookingHours", value)
                }
                min={0}
                max={72}
                suffix="hrs"
              />
              {errors.minAdvanceBookingHours && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.minAdvanceBookingHours}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Minimum notice required
              </p>
            </div>
          </div>

          {/* Boolean Preferences */}
          <div className="pt-4 border-t space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={preferences.allowWalkIns}
                onChange={(e) =>
                  updatePreference("allowWalkIns", e.target.checked)
                }
              />
              <div>
                <p className="font-medium text-sm text-gray-900">
                  Allow Walk-ins
                </p>
                <p className="text-xs text-gray-600">
                  Accept customers without appointments
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={preferences.allowOnlineBooking}
                onChange={(e) =>
                  updatePreference("allowOnlineBooking", e.target.checked)
                }
              />
              <div>
                <p className="font-medium text-sm text-gray-900">
                  Allow Online Booking
                </p>
                <p className="text-xs text-gray-600">
                  Let customers book through website/app
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t pt-4 flex justify-between items-center">
          <p className="text-sm text-orange-600 font-medium flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4" />
            You have unsaved changes
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading || getActiveErrorsCount(errors) > 0}
            >
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Appointment Settings Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Select only appointment types you're comfortable handling</li>
              <li>Specializations help customers find the right expert</li>
              <li>Buffer time prevents back-to-back appointments</li>
              <li>Use presets to quickly configure common setups</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentSettings;
